import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, Eye, Loader2 } from 'lucide-react';
import { useImpersonationStore } from '@/store/impersonation';
import { exitImpersonation, revalidateImpersonationSession } from '@/services/impersonation';
import { Button } from '@/components/ui';

export function ImpersonationBanner() {
  const targetSummary = useImpersonationStore((s) => s.targetSummary);
  const sessionId = useImpersonationStore((s) => s.sessionId);
  const mode = useImpersonationStore((s) => s.mode);
  const [exiting, setExiting] = useState(false);
  const [validating, setValidating] = useState(!!sessionId);
  const navigate = useNavigate();

  useEffect(() => {
    if (!sessionId) {
      setValidating(false);
      return undefined;
    }
    let cancelled = false;
    setValidating(true);
    void revalidateImpersonationSession().finally(() => {
      if (!cancelled) {
        setValidating(false);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [sessionId]);

  if (validating || !targetSummary || !mode) {
    return null;
  }

  const displayName =
    `${targetSummary.firstName} ${targetSummary.lastName}`.trim() || targetSummary.email;

  const isReadWrite = mode === 'read_write';

  const handleExit = async () => {
    setExiting(true);
    try {
      await exitImpersonation();
      navigate('/backoffice/users');
    } catch (error) {
      console.error('Failed to exit impersonation:', error);
    } finally {
      setExiting(false);
    }
  };

  return (
    <div
      className={
        isReadWrite
          ? 'flex shrink-0 items-center justify-between gap-4 border-b border-red-700/40 bg-red-600 px-4 py-2 text-sm text-white'
          : 'flex shrink-0 items-center justify-between gap-4 border-b border-amber-600/30 bg-amber-500 px-4 py-2 text-sm text-amber-950'
      }
      role="status"
      aria-live="polite"
    >
      <div className="flex min-w-0 items-center gap-2">
        {isReadWrite ? (
          <AlertTriangle className="h-4 w-4 shrink-0" aria-hidden />
        ) : (
          <Eye className="h-4 w-4 shrink-0" aria-hidden />
        )}
        <span className="truncate">
          Viewing as <strong className="font-semibold">{displayName}</strong>
          {isReadWrite ? (
            <span> (read &amp; write — actions affect this user&apos;s account)</span>
          ) : (
            <span> (read-only)</span>
          )}
        </span>
      </div>
      <Button
        type="button"
        variant="outline"
        size="sm"
        className={
          isReadWrite
            ? 'shrink-0 border-white/40 bg-red-700 text-white hover:bg-red-800 hover:text-white'
            : 'shrink-0 border-amber-800/40 bg-amber-50 text-amber-950 hover:bg-amber-100'
        }
        onClick={() => void handleExit()}
        disabled={exiting}
      >
        {exiting ? (
          <>
            <Loader2 className="mr-2 h-3 w-3 animate-spin" aria-hidden />
            Exiting…
          </>
        ) : (
          'Exit impersonation'
        )}
      </Button>
    </div>
  );
}
